import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Project } from '@/types'
import { Search, FolderPlus, LayoutGrid, List } from 'lucide-react'
import { cn } from '@/utils/cn'
import { canCreateProject } from '@/utils/permissions'
import { EmptyState } from '@/components/EmptyState'
import { ProjectCard } from './ProjectCard'
import { ProjectListRow } from './ProjectListRow'

type ViewMode = 'grid' | 'list'
type SortBy = 'updated' | 'name' | 'created'

interface DashboardProjectsProps {
  projects: Project[]
  user: any
  searchTerm: string
  onSearchChange: (term: string) => void
  sortBy: SortBy
  onSortByChange: (sort: SortBy) => void
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  onNewProject: () => void
  userCanCreateProject: boolean
}

export const DashboardProjects = ({
  projects,
  user,
  searchTerm,
  onSearchChange,
  sortBy,
  onSortByChange,
  viewMode,
  onViewModeChange,
  onNewProject,
  userCanCreateProject,
}: DashboardProjectsProps) => {
  const { t } = useTranslation(['dashboard', 'common'])

  const filteredProjects = useMemo(() => {
    const filtered = projects.filter((project) => {
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase()
        const projectName = project?.name?.toLowerCase() || ''
        const agencyName = (project as any).agencies?.name?.toLowerCase() || ''
        if (!projectName.includes(searchLower) && !agencyName.includes(searchLower)) {
          return false
        }
      }

      return true
    })

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '')
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'updated':
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      }
    })

    return filtered
  }, [projects, searchTerm, sortBy])

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-framed-neutral-500" />
          <input
            type="text"
            placeholder={t('dashboard:search.placeholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-12 pl-10 pr-4 bg-white border border-framed-neutral-300 rounded-xl text-framed-neutral-800 placeholder-framed-neutral-500 focus:outline-none focus:ring-2 focus:ring-framed-yellow/40 focus:border-transparent transition-shadow"
          />
        </div>

        {/* Sort By */}
        <select
          value={sortBy}
          onChange={(e) => onSortByChange(e.target.value as SortBy)}
          className="h-12 px-4 bg-white border border-framed-neutral-300 rounded-xl text-framed-neutral-800 focus:outline-none focus:ring-2 focus:ring-framed-yellow/40 focus:border-transparent transition-shadow"
        >
          <option value="updated">{t('dashboard:filters.lastModified')}</option>
          <option value="name">{t('dashboard:filters.name')}</option>
          <option value="created">{t('dashboard:filters.createdDate')}</option>
        </select>

        {/* View Toggle */}
        <div className="flex items-center bg-white border border-framed-neutral-300 rounded-xl overflow-hidden">
          <button
            onClick={() => onViewModeChange('grid')}
            className={cn(
              'h-12 px-3 flex items-center justify-center transition-colors',
              viewMode === 'grid'
                ? 'bg-framed-neutral-100 text-framed-neutral-800'
                : 'text-framed-neutral-400 hover:text-framed-neutral-600'
            )}
            title="Grid view"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={cn(
              'h-12 px-3 flex items-center justify-center transition-colors',
              viewMode === 'list'
                ? 'bg-framed-neutral-100 text-framed-neutral-800'
                : 'text-framed-neutral-400 hover:text-framed-neutral-600'
            )}
            title="List view"
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Project Display */}
      {filteredProjects.length === 0 ? (
        searchTerm.trim() ? (
          <EmptyState
            icon={Search}
            title={t('common:empty.noResults')}
            description={t('dashboard:search.tryAnother')}
            iconColor="text-framed-neutral-400"
          />
        ) : (
          <EmptyState
            icon={FolderPlus}
            title={t('dashboard:empty.title')}
            description={
              userCanCreateProject
                ? t('dashboard:empty.subtitle')
                : t('dashboard:empty.noAssigned')
            }
            iconColor="text-framed-yellow-500"
            action={userCanCreateProject ? {
              label: t('dashboard:buttons.newProject'),
              onClick: onNewProject,
            } : undefined}
          />
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              user={user}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProjects.map((project) => (
            <ProjectListRow
              key={project.id}
              project={project}
              user={user}
            />
          ))}
        </div>
      )}
    </div>
  )
}
